import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";

export interface DateRange {
  from?: Date;
  to?: Date;
}

export interface DateRangePickerProps {
  from?: Date;
  to?: Date;
  onSelect?: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
  fromLabel?: string;
  toLabel?: string;
}

/**
 * DateRangePicker component using two DatePicker instances side-by-side.
 * Validates that "to" date is not before "from" date.
 */
export function DateRangePicker({
  from,
  to,
  onSelect,
  disabled,
  className,
  fromLabel,
  toLabel,
}: DateRangePickerProps) {
  const { i18n } = useLingui();

  const handleFromChange = (date: Date | undefined) => {
    // If "to" date exists and new "from" is after "to", sync "to" to match "from"
    const newRange: DateRange = { from: date, to };
    if (date && to && date > to) {
      newRange.to = date;
    }
    onSelect?.(newRange);
  };

  const handleToChange = (date: Date | undefined) => {
    // If "from" date exists and new "to" is before "from", sync "from" to match "to"
    const newRange: DateRange = { from, to: date };
    if (date && from && date < from) {
      newRange.from = date;
    }
    onSelect?.(newRange);
  };

  return (
    <div className={cn("flex flex-col gap-4 sm:flex-row sm:gap-2", className)}>
      <div className="flex flex-col gap-2 flex-1">
        <Label htmlFor="date-from" className="text-sm">
          {fromLabel ?? <Trans>From</Trans>}
        </Label>
        <DatePicker
          id="date-from"
          value={from}
          onChange={handleFromChange}
          placeholder={i18n._({ id: 'datepicker.selectStartDate', message: 'Select start date' })}
          disabled={disabled}
          showClearButton={true}
        />
      </div>
      <div className="flex flex-col gap-2 flex-1">
        <Label htmlFor="date-to" className="text-sm">
          {toLabel ?? <Trans>To</Trans>}
        </Label>
        <DatePicker
          id="date-to"
          value={to}
          onChange={handleToChange}
          placeholder={i18n._({ id: 'datepicker.selectEndDate', message: 'Select end date' })}
          disabled={disabled}
          showClearButton={true}
        />
      </div>
    </div>
  );
}
